import { createElement } from 'react'
import { Helmet } from 'react-helmet-async'

import { createMetaTags } from 'utils/seo/meta-tags/index.mjs'

import { getLanguageCode, translate } from '../../utils/translation'
import { ROOT_URL, SUPPORTED_LANGS } from '../../utils/consts.mjs'

interface MetaTag {
  tag: keyof JSX.IntrinsicElements
  text?: string | null
  props?: Record<string, string | undefined>
}

interface Canonical {
  pageKey: string
  path: string
}

interface TagItem {
  tags: MetaTag[]
  canonical?: Canonical
}

interface HelmetReactProps {
  path?: string
  pageKey?: string | null
}

const getHref = ({ lang, path }: { lang: string; path: string }): string => {
  if (!path) return ''
  return `${ROOT_URL}/${lang}/${path}/`
}

export const HelmetReact = ({
  path = '',
  pageKey = null,
}: HelmetReactProps) => {
  const currentPageKey = pageKey || path
  const META_TAGS: Record<string, TagItem> = createMetaTags()
  const tagItem = META_TAGS[currentPageKey] || {}
  const currentLanguage = getLanguageCode()

  return (
    <Helmet key={currentPageKey} htmlAttributes={{ lang: currentLanguage }}>
      {tagItem.tags.map((item, index) => {
        const { tag, text = null, props = {} } = item
        if (props.content) props.content = translate(props.content || '')
        return createElement(
          tag,
          { ...props, key: index },
          translate(text || ''),
        )
      })}
      {tagItem?.canonical?.pageKey === currentPageKey && (
        <link
          rel="canonical"
          href={getHref({
            lang: currentLanguage,
            path: tagItem?.canonical?.path || '',
          })}
        />
      )}
      {SUPPORTED_LANGS.map((lang) => (
        <link
          key={lang}
          rel="alternate"
          href={getHref({ lang, path })}
          hrefLang={lang}
        />
      ))}
      <link
        rel="alternate"
        href={getHref({ lang: currentLanguage, path })}
        hrefLang="x-default"
      />
    </Helmet>
  )
}
